import {
  searchProviderLabel,
  YANDEX_FALLBACK_LABELS,
} from '../../../api/src/search/search-provider.types';
import { EditorOnly } from '../components/EditorOnly';
import { Icon } from '@iconify/react';
import {
  Alert,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  MenuItem,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tabs,
  TextField,
} from '@mui/material';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { api } from '../api/client';
import { sectionById } from '../api/generated/section-catalog';
import type {
  SearchSnapshotPage,
  FakesKeywordList,
  FakesResult,
  FakesResultPage,
} from '../api/types';
import { CURRENT_CLASSIFICATIONS } from '../../../api/src/fakes/serp-classification';
import type { FakeClassification } from '../../../api/src/fakes/fakes.types';
import { FakesMatrixPage } from './FakesMatrixPage';
import { FakeResultDrawer } from '../components/FakeResultDrawer';
import { fakeLabels as labels } from '../components/fakes-presentation';
import { PageHeader } from '../components/PageHeader';
import { Box, Stack, Typography } from '../components/mui-system';
import { useWorkspace } from '../workspace-context';

const date = (value: string | null | undefined) =>
  value ? new Date(value).toLocaleString('ru-RU') : 'ещё не загружен';
const message = (error: unknown) =>
  error instanceof Error ? error.message : 'Не удалось выполнить действие';

export function FakesPage() {
  const { keywordId } = useParams<{ keywordId: string }>();
  return keywordId ? <LegacyFakesPage /> : <FakesMatrixPage />;
}

function LegacyFakesPage() {
  const { activeWorkspace } = useWorkspace();
  return <FakesWorkspace key={activeWorkspace!.id} workspaceId={activeWorkspace!.id} />;
}

function FakesWorkspace({ workspaceId }: { workspaceId: string }) {
  const navigate = useNavigate();
  const { keywordId = '' } = useParams<{ keywordId: string }>();
  const [list, setList] = useState<FakesKeywordList | null>(null);
  const [results, setResults] = useState<FakesResultPage | null>(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [history, setHistory] = useState<SearchSnapshotPage | null>(null);
  const [historyPage, setHistoryPage] = useState(0);
  const [historyError, setHistoryError] = useState('');
  const [snapshotId, setSnapshotId] = useState('');
  const [filter, setFilter] = useState<FakeClassification | ''>('');
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [revision, setRevision] = useState(0);
  const [selected, setSelected] = useState<FakesResult | null>(null);
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  const keyword = list?.items.find((item) => item.id === keywordId);
  const collecting = Boolean(list?.items.some((item) => item.status === 'collecting'));

  useEffect(() => {
    setSnapshotId('');
    setPage(0);
    setHistoryOpen(false);
    setSelected(null);
  }, [keywordId]);

  const loadList = useCallback(async () => {
    const next = await api.fakesKeywords(workspaceId);
    if (mounted.current) setList(next);
  }, [workspaceId]);

  useEffect(() => {
    void loadList().catch((caught) => {
      if (mounted.current) setError(message(caught));
    });
  }, [loadList]);
  useEffect(() => {
    if (!keywordId && list?.items[0])
      navigate(`${sectionById('fakes').path}/${list.items[0].id}`, { replace: true });
  }, [keywordId, list, navigate]);
  useEffect(() => {
    if (!collecting) return;
    let pending = false;
    const timer = window.setInterval(() => {
      if (pending) return;
      pending = true;
      void loadList()
        .catch((caught) => {
          if (mounted.current) setError(message(caught));
        })
        .finally(() => {
          pending = false;
        });
    }, 5000);
    return () => window.clearInterval(timer);
  }, [collecting, loadList]);

  useEffect(() => {
    let cancelled = false;
    setResults(null);
    if (!keywordId || !keyword) return;
    setLoading(true);
    void api
      .fakesResults(workspaceId, keywordId, {
        page: page + 1,
        pageSize,
        snapshotId: snapshotId || undefined,
        classification: filter || undefined,
      })
      .then((next) => {
        if (!cancelled) setResults(next);
      })
      .catch((caught) => {
        if (!cancelled) setError(message(caught));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [workspaceId, keywordId, keyword, page, pageSize, snapshotId, filter, revision]);

  useEffect(() => {
    if (!historyOpen || !keywordId) return;
    let cancelled = false;
    setHistory(null);
    setHistoryError('');
    void api
      .fakesSnapshots(workspaceId, keywordId, historyPage + 1)
      .then((next) => {
        if (!cancelled) setHistory(next);
      })
      .catch((caught) => {
        if (!cancelled) setHistoryError(message(caught));
      });
    return () => {
      cancelled = true;
    };
  }, [workspaceId, keywordId, historyOpen, historyPage]);

  async function act(action: () => Promise<void>) {
    if (busy) return;
    setBusy(true);
    setError('');
    try {
      await action();
      if (mounted.current) {
        await loadList();
        setRevision((value) => value + 1);
      }
    } catch (caught) {
      if (mounted.current) setError(message(caught));
    } finally {
      if (mounted.current) setBusy(false);
    }
  }
  const registry = results?.registry ?? list?.registry;
  const snapshot = results?.snapshot;
  return (
    <>
      <PageHeader
        title="Карта выдачи"
        description="История отдельного запроса. Новые сборы запускаются из общей матрицы."
        action={
          <Button variant="outlined" onClick={() => navigate(sectionById('fakes').path)}>
            К матрице запросов
          </Button>
        }
      />
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      <Stack gap={2.5}>
        <Card>
          <CardContent>
            <Stack
              direction="row"
              gap={2}
              alignItems="center"
              justifyContent="space-between"
              flexWrap="wrap"
            >
              <Box>
                <Typography fontWeight={750}>Список доменов n8n</Typography>
                <Typography color="text.secondary" fontSize={13}>
                  {registry
                    ? `${registry.count} доменов · ${date(registry.syncedAt)}`
                    : 'Загрузка…'}
                </Typography>
              </Box>
              <EditorOnly permissions="fakes.run">
                <Button
                  variant="outlined"
                  disabled={busy || !registry?.configured}
                  onClick={() =>
                    void act(async () => {
                      const next = await api.syncFakesRegistry(workspaceId);
                      if (mounted.current && next.error) setError(next.error);
                    })
                  }
                >
                  Обновить список n8n
                </Button>
              </EditorOnly>
            </Stack>
            {registry?.stale && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                {!registry.configured
                  ? 'Webhook реестра не настроен на сервере.'
                  : registry.error || 'Реестр ещё не загружен или устарел.'}{' '}
                Принадлежность неизвестных адресов не подтверждена.
              </Alert>
            )}
          </CardContent>
        </Card>
        <Card>
          <CardContent sx={{ p: 0 }}>
            <Stack
              p={2.5}
              direction="row"
              gap={2}
              flexWrap="wrap"
              alignItems="center"
              justifyContent="space-between"
            >
              <Box>
                <Typography variant="h3">Проверка поисковой выдачи</Typography>
                <Typography fontSize={13} color="text.secondary" mt={0.5}>
                  Сохранённая выдача по выбранному ключевому запросу.
                </Typography>
              </Box>
              <Button
                variant="outlined"
                disabled={!keyword}
                onClick={() => {
                  setHistoryPage(0);
                  setHistoryOpen(true);
                }}
                startIcon={<Icon icon="solar:history-linear" />}
              >
                История срезов
              </Button>
            </Stack>
            {list && !list.providerConfigured && (
              <Alert severity="warning" sx={{ mx: 2.5, mb: 2 }}>
                Провайдеры выдачи не настроены на сервере. Сохранённые срезы доступны для просмотра.
              </Alert>
            )}
            {!!list?.items.length && (
              <Tabs
                value={keyword?.id ?? false}
                onChange={(_event, id: string) => {
                  setSnapshotId('');
                  setPage(0);
                  setHistoryOpen(false);
                  setSelected(null);
                  navigate(`${sectionById('fakes').path}/${id}`);
                }}
                variant="scrollable"
                scrollButtons="auto"
                sx={{ px: 1, borderBottom: '1px solid', borderColor: 'divider' }}
              >
                {list.items.map((item) => (
                  <Tab
                    key={item.id}
                    value={item.id}
                    label={item.query}
                    icon={
                      item.status === 'collecting' ? (
                        <Icon icon="solar:refresh-circle-linear" />
                      ) : undefined
                    }
                    iconPosition="start"
                  />
                ))}
              </Tabs>
            )}
            {!list && !error && <LinearProgress />}
            {list && !list.items.length && (
              <Box p={4}>
                <Typography>
                  Добавьте ключевой запрос, чтобы получить первый срез выдачи.
                </Typography>
              </Box>
            )}
            {keywordId && list && !keyword && (
              <Alert severity="error">
                Запрос не найден в разделе «Карта выдачи» этого workspace.
              </Alert>
            )}
            {keyword && (
              <>
                <Stack p={2.5} direction="row" gap={1} flexWrap="wrap" alignItems="center">
                  <Chip label={searchProviderLabel(keyword)} />
                  <Chip label={keyword.searchEngine === 'yandex' ? 'Яндекс' : 'Google'} />
                  <Chip label={keyword.regionName} />
                  <Chip label={keyword.device === 'desktop' ? 'Desktop' : 'Mobile'} />
                  <Chip label={`Top-${keyword.depth}`} />
                  <Typography fontSize={12} color="text.secondary">
                    Снято: {date(snapshot?.capturedAt ?? keyword.lastCapturedAt)}
                  </Typography>
                </Stack>
                {snapshotId && (
                  <Alert
                    severity="info"
                    action={
                      <Button
                        color="inherit"
                        onClick={() => {
                          setSnapshotId('');
                          setPage(0);
                        }}
                      >
                        Текущий срез
                      </Button>
                    }
                  >
                    Исторический срез. Классификация использует текущий реестр и решения.
                  </Alert>
                )}
                {keyword.status === 'collecting' && (
                  <Alert severity="info">
                    Провайдер собирает новый срез. Предыдущая выдача сохраняется до завершения.
                  </Alert>
                )}
                {keyword.status === 'failed' && (
                  <Alert severity="error">
                    Последняя попытка не удалась: {keyword.lastError}. Сохранённая выдача не
                    заменена.
                  </Alert>
                )}
                <Stack px={2.5} pb={2} direction="row" gap={2} flexWrap="wrap" alignItems="center">
                  <TextField
                    select
                    size="small"
                    label="Классификация"
                    value={filter}
                    onChange={(event) => {
                      setFilter(event.target.value as FakeClassification | '');
                      setPage(0);
                    }}
                    sx={{ minWidth: 220 }}
                  >
                    <MenuItem value="">Все результаты</MenuItem>
                    {CURRENT_CLASSIFICATIONS.map((key) => (
                      <MenuItem key={key} value={key}>
                        {labels[key]}
                        {results ? ` (${results.counts[key] ?? 0})` : ''}
                      </MenuItem>
                    ))}
                  </TextField>
                  <Typography fontSize={12} color="text.secondary">
                    {results?.previousSnapshot
                      ? `Сравнение URL со срезом ${date(results.previousSnapshot.capturedAt)}`
                      : 'Предыдущего сопоставимого среза нет'}
                  </Typography>
                </Stack>
                {loading && <LinearProgress />}
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Ресурс</TableCell>
                        <TableCell align="right">Позиция</TableCell>
                        <TableCell align="right">Динамика</TableCell>
                        <TableCell>Классификация</TableCell>
                        <TableCell>Основание</TableCell>
                        <TableCell align="right">Решение</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {results?.items.map((row) => (
                        <TableRow key={row.id} hover>
                          <TableCell>
                            <Typography
                              component="a"
                              href={row.url}
                              target="_blank"
                              rel="noreferrer"
                              fontWeight={750}
                              color="primary.main"
                              fontSize={13}
                            >
                              {row.telegramUsername ? `@${row.telegramUsername}` : row.hostname}
                            </Typography>
                            <Typography
                              fontSize={11}
                              color="text.secondary"
                              sx={{ maxWidth: 330, overflowWrap: 'anywhere' }}
                            >
                              {row.url}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">{row.rank}</TableCell>
                          <TableCell align="right">
                            {row.delta === null
                              ? results.previousSnapshot
                                ? 'Новый URL'
                                : '—'
                              : row.delta === 0
                                ? '—'
                                : row.delta > 0
                                  ? `↑ ${row.delta}`
                                  : `↓ ${Math.abs(row.delta)}`}
                          </TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              color={
                                row.classification === 'fake' || row.classification === 'conflict'
                                  ? 'error'
                                  : row.classification === 'owned' || row.classification === 'donor'
                                    ? 'success'
                                    : 'default'
                              }
                              label={labels[row.classification]}
                            />
                            {row.domainStatus && (
                              <Typography fontSize={11} mt={0.5}>
                                {row.domainStatus}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell sx={{ maxWidth: 320 }}>
                            <Typography fontSize={12}>{row.reason}</Typography>
                            {row.donorHostKnown && row.classification !== 'donor' && (
                              <Typography fontSize={11} color="text.secondary">
                                На хосте есть записи доноров
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell align="right">
                            <Button size="small" disabled={busy} onClick={() => setSelected(row)}>
                              Разобрать
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                      {results && !results.items.length && (
                        <TableRow>
                          <TableCell colSpan={6}>
                            {results.snapshot
                              ? 'Нет результатов с выбранной классификацией.'
                              : 'Первый срез ещё не получен.'}
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
                <TablePagination
                  component="div"
                  count={results?.total ?? 0}
                  page={page}
                  rowsPerPage={pageSize}
                  rowsPerPageOptions={[25, 50, 100]}
                  labelRowsPerPage="Строк на странице"
                  onPageChange={(_event, value) => setPage(value)}
                  onRowsPerPageChange={(event) => {
                    setPageSize(Number(event.target.value));
                    setPage(0);
                  }}
                />
              </>
            )}
          </CardContent>
        </Card>
      </Stack>
      <Dialog open={historyOpen} onClose={() => setHistoryOpen(false)} fullWidth maxWidth="md">
        <DialogTitle>История срезов</DialogTitle>
        <DialogContent>
          {historyError && <Alert severity="error">{historyError}</Alert>}
          {!history && !historyError && <LinearProgress />}
          <Stack gap={1}>
            {history?.items.map((item) => (
              <Box
                key={item.id}
                display="flex"
                gap={2}
                alignItems="center"
                justifyContent="space-between"
              >
                <Box>
                  <Typography>
                    {searchProviderLabel(item)}
                    {item.yandexFallbackReason
                      ? ` · ${YANDEX_FALLBACK_LABELS[item.yandexFallbackReason]}`
                      : ''}{' '}
                    · {date(item.capturedAt)} ·{' '}
                    {item.status === 'complete' ? `${item.resultCount} URL` : 'Ошибка'}
                  </Typography>
                  {item.error && <Typography color="error">{item.error}</Typography>}
                </Box>
                <Button
                  disabled={item.status !== 'complete'}
                  onClick={() => {
                    setSnapshotId(item.isCurrent ? '' : item.id);
                    setPage(0);
                    setHistoryOpen(false);
                  }}
                >
                  Открыть
                </Button>
              </Box>
            ))}
          </Stack>
          <TablePagination
            component="div"
            count={history?.total ?? 0}
            page={historyPage}
            rowsPerPage={10}
            rowsPerPageOptions={[10]}
            onPageChange={(_event, value) => setHistoryPage(value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHistoryOpen(false)}>Закрыть</Button>
        </DialogActions>
      </Dialog>
      {selected && (
        <FakeResultDrawer
          key={selected.id}
          workspaceId={workspaceId}
          result={selected}
          query={keyword?.query}
          capturedAt={snapshot?.capturedAt}
          previousCapturedAt={results?.previousSnapshot?.capturedAt}
          onClose={() => setSelected(null)}
          onSaved={async () => {
            await loadList();
            setRevision((value) => value + 1);
          }}
        />
      )}
    </>
  );
}
